"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { motion, useScroll, useSpring } from "framer-motion";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const fallbackPanels = [{
  name: "Evening Wear",
  description: "Sophisticated silhouettes crafted for gala events and formal celebrations.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_evening_wear.webp",
  imageAlt: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_evening_alt.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_evening_wear.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_evening_wear.webp-wvc-sizes",
  count: 32,
  link: "/ShopPage"
}, {
  name: "Modern Casual",
  description: "Effortless everyday luxury designed for the contemporary fashion enthusiast.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_modern_casual.png",
  imageAlt: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_casual_alt.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_modern_casual.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_modern_casual.png-wvc-sizes",
  count: 45,
  link: "/ShopPage"
}, {
  name: "Tailored Suits",
  description: "Precision-cut garments that define professional excellence and sharp style.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_tailored_suits.webp",
  imageAlt: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_suits_alt.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_tailored_suits.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_tailored_suits.webp-wvc-sizes",
  count: 18,
  link: "/ShopPage"
}];
function CategoryPanels() {
  const {
    terms,
    loading,
    error
  } = useEcommerceTerms();
  if (loading) {
    return <>
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="relative aspect-[4/5] w-full bg-secondary animate-pulse rounded-lg" />)}
      </>;
  }
  const panels = !error && terms && terms.length > 0 ? terms.slice(0, 3).map((term, i) => ({
    name: term.name,
    description: term.description || fallbackPanels[i]?.description || "",
    image: term.image_url || fallbackPanels[i]?.image || "",
    imageAlt: fallbackPanels[i]?.imageAlt || "",
    srcset: fallbackPanels[i]?.srcset,
    sizes: fallbackPanels[i]?.sizes,
    count: term.count ?? fallbackPanels[i]?.count ?? 0,
    link: term.link || fallbackPanels[i]?.link || "/ShopPage",
    index: i
  })) : fallbackPanels.map((p, i) => ({
    ...p,
    index: i
  }));
  return <>
      {panels.map((panel, i) => <Link key={i} data-index={i} to={panel.link} className="group relative block aspect-[4/5] w-full overflow-hidden bg-secondary rounded-lg shadow-md transition-all duration-500 hover:shadow-[0px_20px_40px_hsla(45,68%,20%,0.15)]">
          {}
          {panel.image ? <img src={panel.image} {...panel.srcset ? {
        "data-wvc-srcset": panel.srcset
      } : {}} {...panel.sizes ? {
        "data-wvc-sizes": panel.sizes
      } : {}} alt={panel.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-all duration-[800ms] ease-out group-hover:scale-[1.05] group-hover:opacity-0" /> : null}

          {}
          {panel.imageAlt ? <img src={panel.imageAlt} alt={`${panel.name} alternate`} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-[800ms] ease-out scale-110 group-hover:scale-[1.05] group-hover:opacity-100" /> : null}

          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />

          <div className="absolute left-6 top-6 z-10 inline-flex items-center gap-2 border border-white/40 bg-black/20 px-3 py-1.5 font-default text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-md rounded-md">
            <span className="text-accent">§</span>
            <span>{String(panel.index + 1).padStart(2, "0")}</span>
            <span className="text-white/40">|</span>
            <span>
              {panel.count} {panel.count === 1 ? "PIECE" : "PIECES"}
            </span>
          </div>

          <div className="absolute bottom-8 left-8 right-8 z-10 text-left">
            <h3 className="heading text-white mb-3" style={{
          fontSize: "1.75rem",
          lineHeight: 1.2,
          letterSpacing: "-0.02em"
        }}>
              {panel.name}
            </h3>
            
            <div className="overflow-hidden">
              <p className="max-w-xs font-default text-sm text-white/70 line-clamp-2 transition-transform duration-500 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100">
                {panel.description}
              </p>
            </div>

            <div className="mt-6 flex items-center gap-2 font-default text-[11px] uppercase tracking-[0.2em] text-accent">
              <span className="relative">
                Explore
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
              </span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.5} />
            </div>
          </div>
        </Link>)}
    </>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 3,
  orderby: "count",
  order: "desc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function CategoryCards() {
  const {
    scrollYProgress
  } = useScroll();
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  return <section data-section-id="2082" id="premiumcategorycards" className="relative w-full overflow-hidden bg-[var(--secondary)] py-28 md:py-40 text-foreground">
      {}
      <div className="absolute right-4 top-1/4 bottom-1/4 z-50 hidden w-px bg-border/30 md:block lg:right-10">
        <motion.div className="w-full bg-accent origin-top" style={{
        scaleY,
        height: '100%'
      }} />
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-8 md:px-12 lg:px-20">
        <div className="mb-10 flex items-center gap-3 font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:text-[13px]">
          <span aria-hidden="true" className="text-accent">§</span>
          <span>02</span>
          <span className="text-border">/</span>
          <span>Lush Collections</span>
        </div>

        <div className="relative grid grid-cols-12 gap-x-6 gap-y-10">
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-default font-bold leading-none text-foreground" style={{
          opacity: 0.03,
          fontSize: "clamp(8rem, 18vw, 16rem)",
          letterSpacing: "-0.05em"
        }}>
            02
          </span>

          <div className="relative z-10 col-span-12 lg:col-span-8">
            <h2 className="heading text-foreground" style={{
            fontSize: "clamp(2.125rem, 4.4vw, 3.875rem)",
            letterSpacing: "-0.025em",
            lineHeight: 1.02
          }}>
              Curated styles,{" "}
              <em className="italic font-normal text-accent">refined</em> elegance.
            </h2>
          </div>

          <div className="relative z-10 col-span-12 flex flex-col justify-end lg:col-span-4">
            <p className="body text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: 1.6,
            letterSpacing: "-0.005em",
            opacity: 0.8
          }}>
              From high-fashion evening wear designed for the spotlight to 
              modern casual pieces that define your daily aesthetic — every 
              garment is crafted with precision and premium materials.
            </p>

            <div className="mt-8 flex items-center gap-3">
              <Button asChild variant="link" className="h-auto p-0 font-default text-xs uppercase tracking-[0.2em] text-foreground underline-offset-[8px] hover:text-accent hover:no-underline group">
                <Link to="/ShopPage" className="inline-flex items-center gap-2">
                  <span className="underline decoration-accent/30 underline-offset-[8px] transition-colors group-hover:decoration-accent">
                    View All Collections
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-16 flex items-center gap-0 md:mt-24">
          <span className="h-px flex-1 bg-border/60" />
          <span className="block h-1.5 w-1.5 bg-accent rotate-45" />
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 md:mt-20 md:grid-cols-3">
          <EcommerceTermsProvider wp_term_query={wp_term_query} className="contents">
            <CategoryPanels />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>;
}